import React, { useState, useEffect, useRef } from 'react';
import { Cross, Add, ReceiptEdit, Refresh } from '../../CustomComponents/SwiftIcon/Icons';
import SwiftModal from '../../CustomComponents/SwiftModal/SwiftModal';
import ConfirmBox from '../../CustomComponents/ConfirmBox/ConfirmBox';
import { COLOR_VARS } from '../../../exports/ColorVars';
import CustomIconButton from '../../Body/CustomChartComponents/CustomIconButton/CustomIconButton';
import { GetCookie } from '../../../exports/CookieManagement';
import ServerRequest from '../../../exports/ServerRequest';
import '../../../css/RaiseTicket/RaiseTicket.css';
import '../../../css/AdminManagement/AdminTable.css';
import Pulse from '../../Loader/Pulse';
import { ArrowUp2, ArrowRight2 } from '../../CustomComponents/SwiftIcon/Icons';


const REQUEST_BASE_URL = process.env.REACT_APP_REQUEST_BASE_URL;

const TicketsManagement = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [filterStatus, setFilterStatus] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [conversations, setConversations] = useState([]);
    const [conversationLoading, setConversationLoading] = useState(false);
    const [conversationError, setConversationError] = useState('');
    const [newMessage, setNewMessage] = useState('');
    const [conversationFiles, setConversationFiles] = useState([]);
    const [sendingMessage, setSendingMessage] = useState(false);
    const messagesEndRef = useRef(null);
    const account_token = GetCookie("token");

    // Helper function to truncate file names
    const truncateFileName = (fileName, maxLength = 20) => {
        if (!fileName) return 'Unknown File';
        if (fileName.length <= maxLength) return fileName;
        const extension = fileName.split('.').pop();
        const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
        const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 4);
        return `${truncatedName}...${extension}`;
    };

    // Convert text to HTML preserving spaces and line breaks
    const convertToHtml = (text) => {
        if (!text) return '';
        return text
            .replace(/ /g, '&nbsp;') // Replace spaces with non-breaking spaces
            .replace(/\n/g, '<br>'); // Replace line breaks with <br> tags
    };

    // Auto-scroll to bottom of messages
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        fetchTickets();
        // Removed automatic refresh on window focus to prevent interruptions during file uploads
    }, []);

    // Auto-scroll when conversations change
    useEffect(() => {
        if (conversations.length > 0) {
            scrollToBottom();
        }
    }, [conversations]);

    const fetchTickets = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await ServerRequest({
                url: `admin/tickets?token=${account_token}&superuser=true`,
                method: 'get'
            });

            console.log(response.data)

            if (response && response.error === false && response.data) {
                setTickets(response.data);
                console.log('Admin tickets loaded:', response.data.length);
            } else if (response && response.data) {
                setTickets(response.data);
            } else {
                console.log('No tickets data in response:', response.data);
                setTickets([]);
            }
        } catch (error) {
            setError(`Failed to load tickets: ${error.response?.data?.message || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const fetchLatestConversation = async (ticketId) => {
        try {
            setConversationLoading(true);
            setConversationError('');
            const token = GetCookie('admin_token');

            const response = await ServerRequest({
                url: `admin/tickets/${ticketId}/conversation?token=${token}`,
                method: 'get'
            });

            if (response && !response.error) {
                setConversations(response.data || []);
            } else {
                setConversationError('Failed to load conversation');
            }
        } catch (error) {
            console.error('Error fetching conversation:', error);
            setConversationError('Failed to load conversation');
        } finally {
            setConversationLoading(false);
        }
    };

    const handleTicketClick = (ticket) => {
        setSelectedTicket(ticket);
        setShowModal(true);
        // Fetch the latest conversation from server instead of using cached data
        fetchLatestConversation(ticket.ticketId);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedTicket(null);
        setConversations([]);
        setNewMessage('');
        setConversationFiles([]);
        setConversationError('');
        // Refresh tickets list to reflect new sorting when modal closes
        fetchTickets();
    };

    const handleConversationFileChange = (e) => {
        const files = Array.from(e.target.files);
        const validFiles = [];
        const errors = [];

        files.forEach(file => {
            const validTypes = ['image/jpeg', 'image/jpg', 'application/pdf', 'image/png', 'image/gif'];
            if (!validTypes.includes(file.type)) {
                errors.push(`${file.name}: Only JPG, JPEG, PNG, GIF, and PDF files are allowed`);
                return;
            }

            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                errors.push(`${file.name}: File size must be less than 5MB`);
                return;
            }

            validFiles.push(file);
        });

        if (errors.length > 0) {
            setConversationError(errors.join(', '));
            return;
        }

        setConversationFiles(prev => [...prev, ...validFiles]);
        setConversationError('');
    };

    const removeConversationFile = (index) => {
        setConversationFiles(prev => prev.filter((_, i) => i !== index));
    };

    const sendMessage = async () => {
        if (!newMessage && conversationFiles.length === 0) return;
        if (sendingMessage) return; // Prevent double sending

        try {
            setSendingMessage(true);
            const token = GetCookie('admin_token');

            // Create FormData for file uploads
            const formData = new FormData();
            formData.append('ticketId', selectedTicket.ticketId);
            formData.append('message', newMessage);

            // Add files to FormData
            conversationFiles.forEach((file) => {
                formData.append('files', file);
            });

            const response = await ServerRequest({
                url: `admin/tickets/send-message?token=${token}`,
                method: 'post',
                data: formData
            });

            if (response && !response.error) {
                setNewMessage('');
                setConversationFiles([]);
                // Refresh the conversation to get the latest messages from TXT file
                fetchLatestConversation(selectedTicket.ticketId);
            } else {
                setConversationError('Failed to send message');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            setConversationError('Failed to send message');
        } finally {
            setSendingMessage(false);
        }
    };

    const handleTicketStatusChange = async (ticketId, isChecked, event) => {
        event.stopPropagation(); // Prevent opening the modal

        const newStatus = isChecked ? 'closed' : 'open';

        // Immediately update the ticket status in local state (optimistic update)
        setTickets(prevTickets =>
            prevTickets.map(ticket =>
                ticket.id === ticketId
                    ? { ...ticket, status: newStatus }
                    : ticket
            )
        );

        // Update selected ticket if it's the one being changed
        if (selectedTicket && selectedTicket.id === ticketId) {
            setSelectedTicket({ ...selectedTicket, status: newStatus });
        }

        try {
            const token = GetCookie('admin_token');

            await ServerRequest({
                url: `admin/tickets/${ticketId}/status?token=${token}`,
                method: 'put',
                data: { status: newStatus }
            });

            console.log('Ticket status updated successfully');
        } catch (error) {
            console.error('Error updating ticket status:', error);
            setError('Failed to update ticket status');
            fetchTickets();
        }
    };

    const handleDeleteTicket = (ticketId, event) => {
        event.stopPropagation();

        ConfirmBox({
            title: 'Delete Ticket',
            description: 'Are you sure you want to delete this ticket? This action cannot be undone.',
            cancel: true,
            properties: [
                {
                    id: 'delete-confirm',
                    label: 'Delete',
                    handleFunction: async (closeConfirmBox) => {
                        // Immediately remove from UI (optimistic update)
                        setTickets(prevTickets =>
                            prevTickets.filter(ticket => ticket.id !== ticketId)
                        );

                        // Close modal if the deleted ticket was selected
                        if (selectedTicket && selectedTicket.id === ticketId) {
                            setShowModal(false);
                            setSelectedTicket(null);
                        }

                        try {
                            const token = GetCookie('admin_token');

                            await ServerRequest({
                                url: `admin/tickets/${ticketId}?token=${token}`,
                                method: 'delete'
                            });

                            console.log('Ticket deleted successfully');
                        } catch (error) {
                            console.error('Error deleting ticket:', error);
                            setError('Failed to delete ticket');

                            // If delete failed, we should reload the tickets to restore the UI
                            fetchTickets();
                        }

                        closeConfirmBox();
                    }
                }
            ]
        });
    };

    const filteredTickets = tickets.filter(ticket => {
        if (!searchTerm?.trim()) return true;

        const normalize = str =>
            (str || "")
                .toLowerCase()
                .replace(/[#\-\s]/g, ""); // remove #, -, and spaces

        const term = normalize(searchTerm);
        const accountName = normalize(ticket.account_name);
        const ticketId = normalize(ticket.ticketId);

        return accountName.includes(term) || ticketId.includes(term);
    });


    const finalFilteredTickets = filteredTickets.filter(ticket => {
        if (filterStatus === 'all') return true;
        return ticket.status === filterStatus;
    });

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Add this function inside your TicketsManagement component, before the return statement
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    if (loading) {
        return (
            <div className='admin-wrapper'>
                <div className="loading-container">
                    <div className="swift-spinner"></div>
                    <Pulse></Pulse>
                    {/* <p>Loading tickets...</p> */}
                </div>
            </div>
        );
    }

    return (
        <div className="support-tickets-container">
            <div className="tickets-header">
                <div className="header-content">
                    <h2>Support Tickets Management
                        <span
                            onClick={fetchTickets}
                            disabled={loading}
                            className=''
                        >
                            <Refresh size={14} color={COLOR_VARS.SWIFT_COLOR4} />
                        </span>
                    </h2>
                    <p>Manage and respond to customer support tickets</p>
                </div>
            </div>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {/* Search */}
            <div className="tickets-search">
                <div className="form-group">

                    <input
                        type="text"
                        className="form-input"
                        placeholder="Search by ticket id or client name ..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />

                </div>
            </div>

            {/* Tickets List */}
            <div className="tickets-content">
                {filteredTickets.length === 0 ? (
                    <div className="no-tickets">
                        <ReceiptEdit size={50} color={COLOR_VARS["SWIFT_COLOR4"]} />
                        <h3>No tickets found</h3>
                        <p>No support tickets match your current filters.</p>
                    </div>
                ) : (
                    <div className="tickets-list">
                        {finalFilteredTickets.map((ticket) => (

                            <div
                                key={ticket.id}
                                className={`ticket-card ${ticket.status === 'closed' ? 'closed' : ''}`}
                            >
                                <div className="ticket-header">
                                    <div className="ticket-id">
                                        <p>{ticket.account_name} </p>
                                        <div>{ticket.subject} ({formatDate(ticket.createdAt)})</div>
                                    </div>
                                    <div className="ticket-status-section">

                                        <div
                                            className="ticket-actions-container"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {(ticket.status === 'open' || ticket.status === 'pending') && (
                                                <span
                                                    className="close-text"
                                                    onClick={(e) => {
                                                        handleTicketStatusChange(ticket.id, true, e)
                                                        console.log(e)
                                                    }}
                                                    title="Close Ticket"
                                                >
                                                    Close
                                                </span>
                                            )}
                                            <span
                                                className="delete-text"
                                                onClick={(e) => handleDeleteTicket(ticket.id, e)}
                                                title="Delete Ticket"
                                            >
                                                Delete
                                            </span>
                                        </div>
                                        <div
                                            onClick={() => handleTicketClick(ticket)}
                                            className={`ticket-status ${ticket.status}`}>
                                            {ticket.status}
                                        </div>
                                    </div>
                                </div>

                                <div className="ticket-details">
                                    <div className="ticket-info">
                                        <div className="ticket-files-section">
                                            <p>{`"${ticket.lastmessage}"`}</p>
                                        </div>
                                        {ticket.filesUploaded > 0 && (
                                            <div>
                                                {(() => {
                                                    // Priority: Latest conversation files > Original ticket files
                                                    const filesToShow = ticket.latestConversationFiles && ticket.latestConversationFiles.length > 0
                                                        ? ticket.latestConversationFiles
                                                        : ticket.fileUrls;

                                                    return filesToShow && filesToShow.length > 0 && (
                                                        <div className="latest-files-preview">
                                                            <div className="files-preview-container">
                                                                {filesToShow.map((file, index) => (
                                                                    <div key={index} className="message-file">
                                                                        <a
                                                                            href={file.url}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                        >
                                                                            {truncateFileName(file.originalName)}
                                                                        </a>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Ticket Detail Modal / Chat Box */}
            {showModal && selectedTicket && (
                <SwiftModal closeModal={closeModal} top="2%">
                    <div className="chat-modal-container">
                        {/* Chat Header */}
                        <div className="chat-header">
                            <div className="chat-header-info">
                                <div className="ticket-id-badge">#{selectedTicket.ticketId}</div>
                                <div className="chat-header-meta">
                                    <h3 className="ticket-subject">{selectedTicket.subject}</h3>
                                    <span className="ticket-client">{selectedTicket.account_name}</span>
                                </div>
                            </div>
                            <div className="modal-close" onClick={closeModal}>
                                <Cross size={24} color={COLOR_VARS["SWIFT_COLOR4"]} />
                            </div>
                        </div>

                        {/* Chat Body (Messages) */}
                        <div className="chat-body">
                            {sendingMessage ? (
                                <div className="conversation-loading"><Pulse /></div>
                            ) : conversationLoading ? (
                                <div className="conversation-loading"><Pulse /></div>
                            ) : conversationError ? (
                                <p className="error-message">{conversationError}</p>
                            ) : conversations.length === 0 ? (
                                <div className="no-messages">
                                    <ReceiptEdit size={40} color={COLOR_VARS["SWIFT_COLOR4"]} />
                                    <p>No messages yet. Start the conversation!</p>
                                </div>
                            ) : (
                                <>
                                    {conversations.map((msg, index) => {
                                        // Assuming 'admin' is the sender name/role for outgoing messages. 
                                        // Adjust the condition based on your actual API response.
                                        const isOutgoing = msg.sender === 'admin' || msg.senderName === 'Admin';

                                        return (
                                            <div key={index} className={`chat-message-wrapper ${isOutgoing ? 'outgoing' : 'incoming'}`}>
                                                <div className="chat-bubble">
                                                    {!isOutgoing && <span className="chat-sender-name">{msg.senderName}</span>}
                                                    <div
                                                        className="chat-text"
                                                        dangerouslySetInnerHTML={{ __html: convertToHtml(msg.message) }}
                                                    />

                                                    {/* File Attachments in Bubble */}
                                                    {msg.fileUrls && msg.fileUrls.length > 0 && (
                                                        <div className="chat-attachments">
                                                            {msg.fileUrls.map((file, fileIndex) => (
                                                                <a key={fileIndex} href={file.url} target="_blank" rel="noopener noreferrer" className="chat-file-link">
                                                                    📎 {truncateFileName(file.originalName)}
                                                                </a>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <span className="chat-time">{formatDate(msg.timestamp)}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div ref={messagesEndRef} />
                                </>
                            )}
                        </div>

                        {/* Chat Footer (Input Area) */}
                        <div className="chat-footer">
                            {/* Uploaded Files Preview */}
                            {conversationFiles.length > 0 && (
                                <div className="chat-uploads-preview">
                                    {conversationFiles.map((file, index) => (
                                        <div key={index} className="chat-file-badge">
                                            <span>{truncateFileName(file.name)}</span>
                                            <div onClick={() => removeConversationFile(index)} className="remove-file">
                                                <Cross size={14} color="#ff4444" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="chat-input-row">
                                <input
                                    type="file"
                                    multiple
                                    accept=".jpg,.jpeg,.png,.gif,.pdf"
                                    onChange={handleConversationFileChange}
                                    style={{ display: 'none' }}
                                    id="chat-file-input"
                                />
                                <label htmlFor="chat-file-input" className="chat-attach-btn">
                                    <Add size={24} color={COLOR_VARS["SWIFT_COLOR4"]} />
                                </label>

                                <textarea
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Type a message... (Press Enter to send)"
                                    className="chat-textarea"
                                    rows="2"
                                />

                                <button
                                    className="chat-send-btn"
                                    onClick={sendMessage}
                                    disabled={(!newMessage.trim() && conversationFiles.length === 0) || sendingMessage}
                                >
                                    <ArrowRight2 size={24} color={(!newMessage.trim() && conversationFiles.length === 0) ? '#ccc' : COLOR_VARS["SWIFT_COLOR4"]} />
                                </button>
                            </div>
                        </div>
                    </div>
                </SwiftModal>
            )}
        </div>
    );
};

export default TicketsManagement;